import { Injectable } from '@angular/core';
import { onShow, showNewMessage, trackEvent } from '@intercom/messenger-js-sdk';
import {
  defaultIfEmpty,
  filter,
  map,
  Observable,
  take,
  takeUntil,
  timer,
} from 'rxjs';

/**
 * SD-2769/SD-2771: opening the Intercom messenger with a question already typed in.
 *
 * <p>Lives beside the Intercom token effects rather than in the chat lib, because this is the
 * second half of the same integration, and a lib cannot reach the app's own IntercomService.
 */

/**
 * `loading` is the window between boot and the widget script arriving. The npm SDK installs a
 * stand-in on `window.Intercom` straight away, so "it exists" does not mean the messenger works:
 * calls made against the stand-in are queued and, if the script never arrives, silently never run.
 */
export type IntercomMessengerState = 'ready' | 'loading' | 'unavailable';

const POLL_MS = 250;
const SETTLE_MS = 5_000;

@Injectable({ providedIn: 'root' })
export class IntercomMessengerService {
  /**
   * Whether the real widget is on the page. The SDK's stand-in is recognisable by the
   * `loaderQueue` it carries; the loaded widget replaces it with its own function.
   */
  snapshot(): IntercomMessengerState {
    const intercom = (window as unknown as { Intercom?: unknown }).Intercom;
    if (typeof intercom !== 'function') return 'unavailable';
    return typeof (intercom as { loaderQueue?: unknown }).loaderQueue ===
      'function'
      ? 'loading'
      : 'ready';
  }

  /**
   * Settles `loading` before answering, so a page that is still fetching the widget is not
   * reported as unavailable. Emits once.
   *
   * Deliberately not based on Intercom's `onShow`: that only fires when the messenger opens from
   * closed, so a messenger the user already has open would be reported as unavailable (SD-2769).
   */
  available(settleMs = SETTLE_MS): Observable<boolean> {
    return timer(0, POLL_MS).pipe(
      map(() => this.snapshot()),
      filter(state => state !== 'loading'),
      map(state => state === 'ready'),
      take(1),
      takeUntil(timer(settleMs)),
      defaultIfEmpty(false)
    );
  }

  /**
   * Opens the messenger with `question` in the composer. The user still presses send themselves —
   * nothing is sent from here. Returns false when the widget is not there, so the caller can fall
   * back to email rather than leaving a control that does nothing.
   */
  openWithMessage(question: string): boolean {
    if (this.snapshot() !== 'ready') return false;
    // Marks the user's activity so support can see the conversation started from Allo. Intercom
    // has no way to tag the conversation itself from the page (SD-2769).
    trackEvent('allo-support-handoff');

    // Intercom fills the composer only when the messenger is already up: a call that has to open
    // it first can land with an empty box. onShow fires exactly in that opened-from-closed case,
    // so send the text again there. When it is already open the first call works and onShow never
    // fires, which is why this is not a blanket retry. One-shot, so a later open by the user does
    // not resurrect an old question.
    let resent = false;
    onShow(() => {
      if (resent) return;
      resent = true;
      showNewMessage(question);
    });
    showNewMessage(question);
    return true;
  }
}
